import './index.scss'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useAuth } from '@auth/AuthProvider'
import LoadingIndicator from '@components/LoadingIndicator'
import Modal from '@components/Modal'
import OptionsForm from '@components/OptionsForm'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import IntegrationsController from '@data/controller/IntegrationsController'
import VolunteeringController from '@data/controller/VolunteeringController'
import VolunteerTeam, { TeamImportResult } from '@data/interfaces/Volunteering/volunteerteam.interface'
import { ExternalVendor } from '@interfaces/external-vendor.interface'
import { ImportableTeam } from '@interfaces/integration.interface'

import backIcon from 'url:@icon/chevron.left.plain.svg';
import closeIcon from 'url:@icon/close.svg';
import pcoLogo from 'url:@image/pco/people-icon.png'
import saltLogo from 'url:@image/salt-icon.png'
import heartIcon from 'url:@assets/icons/heart.fill.svg'

// Where the new team comes from, then the step for that source.
type CreateTeamStage = 'source' | 'details' | 'import';

// The stage being left, kept mounted long enough to slide out from under the
// one arriving.
type StageTransition = {
  from: CreateTeamStage;
  direction: 'forward' | 'back';
};

// Matches the slide in index.scss.
const STAGE_TRANSITION_MS = 240;

// ImportTeamForChurch's refusal for a team imported before.
const TEAM_ALREADY_IMPORTED = 'TEAM_ALREADY_IMPORTED';

// Same choices as creating an event -- build a team in Salt, or bring one over
// from a connected provider -- as steps in one modal. The caller keys it per
// open, so every open starts back at the source step.
export default function CreateTeamModal(props: {
  isOpen: boolean,
  close: () => void,
  onCreated: (team: VolunteerTeam, name: string) => void
}) {
  const auth = useAuth();
  const { t } = useTranslation(['createTeam', 'volunteering']);
  const { connectionStatus, reloadConnectionStatus } = useChurchDataProvider();

  const [stage, setStage] = useState<CreateTeamStage>('source');
  const [transition, setTransition] = useState<StageTransition | null>(null);
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The import step's data lives here rather than in the step: the step is
  // rendered a second time as it slides out, and a copy that fetched for itself
  // would request the list twice and flash a spinner on its way out.
  const [importableTeams, setImportableTeams] = useState<ImportableTeam[] | null>(null);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [teamFilter, setTeamFilter] = useState('');
  const [selectedExternalID, setSelectedExternalID] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<TeamImportResult | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  const incomingRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Ensure we have the latest connection status whenever the modal opens
    if (props.isOpen) {
      reloadConnectionStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  useEffect(() => {
    if (!transition) return;

    const timeout = window.setTimeout(() => setTransition(null), STAGE_TRANSITION_MS);

    return () => window.clearTimeout(timeout);
  }, [transition]);

  // The steps differ a lot in height -- the source step is far taller than
  // the name form -- so the viewport eases between them. Left to follow the
  // incoming step, it would snap to the new height on the first frame and clip
  // the outgoing step before it could slide away.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    if (!transition) {
      viewport.style.height = '';
      return;
    }

    const fromHeight = outgoingRef.current?.offsetHeight ?? 0;
    const toHeight = incomingRef.current?.offsetHeight ?? 0;

    viewport.style.height = `${fromHeight}px`;
    // Commit the starting height so the change below transitions from it.
    void viewport.offsetHeight;
    viewport.style.height = `${toHeight}px`;
  }, [transition]);

  // Not autoFocus: the field would take focus while it's still parked off to
  // the right, and the browser would scroll the viewport to reveal it --
  // jumping both steps to their end positions instead of sliding them.
  useEffect(() => {
    if (stage === 'details') {
      nameInputRef.current?.focus({ preventScroll: true });
    }
  }, [stage]);

  const changeStage = (nextStage: CreateTeamStage) => {
    if (nextStage === stage) return;

    // The source step is the root; every other step sits forward of it.
    setTransition({ from: stage, direction: nextStage === 'source' ? 'back' : 'forward' });
    setStage(nextStage);
    setError(null);
  };

  const isPlanningCenterConnected = connectionStatus?.PLANNING_CENTER === true;

  const fetchImportableTeams = async () => {
    if (!auth.churchId) return;

    setIsLoadingTeams(true);
    setTeamsError(null);

    try {
      const response = await IntegrationsController.fetchImportableTeams(
        auth.churchId,
        ExternalVendor.PLANNING_CENTER,
        auth
      );

      if (response.error || !response.body) {
        setTeamsError(response.error || t('loadTeamsFailed'));
        return;
      }

      setImportableTeams(response.body.result ?? []);
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Read live from Planning Center, so only fetched once the step is reached.
  useEffect(() => {
    if (stage !== 'import' || importableTeams !== null || isLoadingTeams || teamsError) return;

    void fetchImportableTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, importableTeams, isLoadingTeams, teamsError]);

  const importSelectedTeam = async () => {
    if (!auth.churchId || !selectedExternalID || isImporting) return;

    setIsImporting(true);
    setError(null);

    try {
      const response = await IntegrationsController.importTeam(
        auth.churchId,
        ExternalVendor.PLANNING_CENTER,
        selectedExternalID,
        auth
      );

      const result = response.body?.result;

      if (!response.error && result) {
        setImportResult(result);
        return;
      }

      if (response.code === TEAM_ALREADY_IMPORTED) {
        // Someone else got there first, or a double-click did. Reload so the
        // list marks it, rather than leaving an option that can't work.
        setError(t('alreadyImported'));
        setSelectedExternalID(null);
        setImportableTeams(null);
        return;
      }

      setError(response.error || t('importFailed'));
    } finally {
      setIsImporting(false);
    }
  };

  const trimmedName = name.trim();
  // The API rejects an empty name.
  const canCreate = trimmedName.length > 0 && !isCreating;

  const createTeam = async () => {
    if (!auth.churchId) {
      setError(t('volunteering:errors.missingChurch'));
      return;
    }

    if (!canCreate) return;

    setIsCreating(true);
    setError(null);

    try {
      const response = await VolunteeringController.createTeam(auth.churchId, trimmedName, auth);
      const created = response.body?.result;

      if (response.error || !created) {
        setError(response.error || t('createFailed'));
        return;
      }

      props.onCreated(created, trimmedName);
    } finally {
      setIsCreating(false);
    }
  };

  // The stage sliding out is rendered a second time while it leaves, so only
  // the copy that's arriving gets the input ref.
  const renderStage = (target: CreateTeamStage, isOutgoing: boolean) => {
    if (target === 'source') {
      return <OptionsForm
        title={t('sourceTitle')}
        description={t('sourceDescription')}
        icon={{
          image: heartIcon,
          alt: t('volunteeringIcon')
        }}
        // Trans fills the link with its text from the catalog.
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        unavailableText={<Trans t={t} i18nKey='unavailable' components={{ link: <a href='/connections' /> }} />}
        cards={[
          {
            title: t('createInSalt'),
            description: t('createInSaltDescription'),
            logo: {
              image: saltLogo,
              alt: t('saltLogo')
            },
            enabled: true,
            onClick: () => changeStage('details')
          },
          {
            title: t('importTitle'),
            description: t('importDescription'),
            logo: {
              image: pcoLogo,
              alt: t('planningCenterLogo')
            },
            enabled: isPlanningCenterConnected,
            onClick: () => changeStage('import')
          }
        ]} />
    }

    if (target === 'details') {
      // Styled after the Create in Salt event form.
      return <form
        className='team-form'
        onSubmit={(e) => {
          e.preventDefault();
          void createTeam();
        }}>
        <div className='section'>
          <div className='input'>
            <input
              type='text'
              name='name'
              placeholder={t('namePlaceholder')}
              aria-label={t('nameLabel')}
              value={name}
              ref={isOutgoing ? undefined : nameInputRef}
              disabled={isCreating}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        <p className='footnote'>{t('detailsFootnote')}</p>

        <button type='submit' className='submit' disabled={!canCreate}>
          {isCreating ? t('creating') : t('create')}
        </button>
      </form>
    }

    return <ImportStage
      teams={importableTeams}
      isLoading={isLoadingTeams}
      loadError={teamsError}
      filter={teamFilter}
      onFilterChange={setTeamFilter}
      selectedExternalID={selectedExternalID}
      onSelect={setSelectedExternalID}
      isImporting={isImporting}
      result={importResult}
      onRetry={() => {
        void fetchImportableTeams();
      }}
      onImport={() => {
        void importSelectedTeam();
      }}
      onCreateInSalt={() => changeStage('details')}
      onGoToTeam={() => {
        if (importResult) {
          props.onCreated(importResult.team, importResult.team.attributes.name);
        }
      }} />
  };

  const isAtSource = stage === 'source';

  let title = '';
  if (stage === 'details') {
    title = t('detailsTitle');
  } else if (stage === 'import') {
    title = t('importTitle');
  }

  return <Modal
    id='create-team-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='create-team-modal-content'>
      <div className='header'>
        <button
          type='button'
          className={isAtSource ? 'header-control close' : 'header-control back'}
          // Going back mid-import would abandon it, and after one the team
          // already exists: the way on is to it.
          disabled={isCreating || isImporting || importResult !== null}
          onClick={() => {
            if (isAtSource) {
              props.close();
              return;
            }

            changeStage('source');
          }}>
          {isAtSource ?
            <img src={closeIcon} alt={t('volunteering:common.close')} /> :
            <img src={backIcon} alt={t('volunteering:common.back')} />}
        </button>

        {/* The source step's own heading already says "New Team". */}
        <h3 className='title'>{title}</h3>

        <span className='header-control-spacer' />
      </div>

      <div className='body'>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='stage-viewport' ref={viewportRef}>
          {transition ? <div
            ref={outgoingRef}
            className={`stage is-outgoing ${transition.direction}`}
            aria-hidden='true'>
            {renderStage(transition.from, true)}
          </div> : null}

          <div ref={incomingRef} className={transition ? `stage is-incoming ${transition.direction}` : 'stage'}>
            {renderStage(stage, false)}
          </div>
        </div>
      </div>
    </div>
  </Modal>
}

// Keeps the API's order -- by service type, then name -- and groups under each
// service type, which is how same-named teams ("Band", "Band") tell apart.
function groupTeams(teams: ImportableTeam[], otherTeamsTitle: string) {
  const groups: { title: string, teams: ImportableTeam[] }[] = [];

  for (const team of teams) {
    const title = team.serviceTypeName || otherTeamsTitle;
    const last = groups[groups.length - 1];

    if (last?.title === title) {
      last.teams.push(team);
    } else {
      groups.push({ title, teams: [team] });
    }
  }

  return groups;
}

// Worth a filter only once the list is long enough to hunt through.
const FILTER_THRESHOLD = 8;

// Lists the church's Planning Center teams, read live, and copies the chosen
// one into a new Salt team with its positions as roles and its people as
// volunteers. A one-time copy: later changes in Planning Center don't follow.
function ImportStage(props: {
  teams: ImportableTeam[] | null;
  isLoading: boolean;
  loadError: string | null;
  filter: string;
  onFilterChange: (filter: string) => void;
  selectedExternalID: string | null;
  onSelect: (externalID: string) => void;
  isImporting: boolean;
  result: TeamImportResult | null;
  onRetry: () => void;
  onImport: () => void;
  onCreateInSalt: () => void;
  onGoToTeam: () => void;
}) {
  const { t } = useTranslation(['createTeam', 'volunteering']);

  if (props.result) {
    const { team, members, roles, unmatchedPeopleCount } = props.result;

    return <div className='import-stage'>
      <h4>{t('importedTitle', { team: team.attributes.name })}</h4>

      <p>
        {t('importedSummary', {
          volunteers: t('importedVolunteers', { count: members.length }),
          roles: t('importedRoles', { count: roles.length })
        })}
      </p>

      {unmatchedPeopleCount > 0 ? <p className='import-notice'>
        <Trans
          t={t}
          i18nKey='unmatched'
          count={unmatchedPeopleCount}
          values={{ count: unmatchedPeopleCount }}
          // Trans fills the link with its text from the catalog.
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          components={{ link: <a href='/people' /> }} />
      </p> : null}

      <button type='button' className='submit' onClick={props.onGoToTeam}>{t('goToTeam')}</button>
    </div>
  }

  if (props.isLoading && !props.teams) {
    return <div className='import-stage import-stage-state'>
      <LoadingIndicator fullScreen={false} />
      <p className='footnote'>{t('loadingTeams')}</p>
    </div>
  }

  if (props.loadError) {
    return <div className='import-stage import-stage-state'>
      <p>{props.loadError}</p>

      <button type='button' className='stage-switch' onClick={props.onRetry}>{t('volunteering:common.tryAgain')}</button>
    </div>
  }

  const teams = props.teams ?? [];

  if (teams.length === 0) {
    return <div className='import-stage import-stage-state'>
      <p>{t('noTeams')}</p>

      <button type='button' className='stage-switch' onClick={props.onCreateInSalt}>
        {t('createInSaltInstead')}
      </button>
    </div>
  }

  const query = props.filter.trim().toLowerCase();
  const visibleTeams = query.length === 0 ? teams : teams.filter((team) => {
    return team.name.toLowerCase().includes(query) ||
      (team.serviceTypeName ?? '').toLowerCase().includes(query);
  });

  const selected = teams.find((team) => team.externalID === props.selectedExternalID) ?? null;

  return <div className='import-stage'>
    <p>{t('chooseTeam')}</p>

    {teams.length > FILTER_THRESHOLD ? <input
      type='search'
      className='importable-team-filter'
      value={props.filter}
      placeholder={t('filterPlaceholder')}
      aria-label={t('filterPlaceholder')}
      disabled={props.isImporting}
      onChange={(e) => props.onFilterChange(e.target.value)}
    /> : null}

    <div className='importable-teams'>
      {visibleTeams.length === 0 ? <p className='footnote'>{t('noFilterMatches')}</p> : null}

      {groupTeams(visibleTeams, t('otherTeams')).map((group) => (
        <div key={group.title} className='importable-team-group'>
          <p className='importable-team-group-title'>{group.title}</p>

          {group.teams.map((team) => {
            const isImported = Boolean(team.importedTeamID);
            const isSelected = team.externalID === props.selectedExternalID;

            return <button
              key={team.externalID}
              type='button'
              className={isSelected ? 'importable-team is-selected' : 'importable-team'}
              aria-pressed={isSelected}
              disabled={isImported || props.isImporting}
              onClick={() => props.onSelect(team.externalID)}>
              <span className='importable-team-name'>{team.name}</span>
              {isImported ? <span className='importable-team-badge'>{t('imported')}</span> : null}
            </button>
          })}
        </div>
      ))}
    </div>

    <p className='footnote'>{t('importFootnote')}</p>

    <button
      type='button'
      className='submit'
      disabled={!selected || props.isImporting}
      onClick={props.onImport}>
      {props.isImporting ? t('importing') : selected ? t('importNamed', { team: selected.name }) : t('import')}
    </button>
  </div>
}
